import { createContext, useContext, useEffect, useState } from 'react';

type Mode = 'light' | 'dark';
type ThemeName = 'moonshot' | 'classic';

interface ThemeContextValue {
  mode: Mode;
  themeName: ThemeName;
  toggleMode: () => void;
  setThemeName: (name: ThemeName) => void;
  /** @deprecated Use mode + toggleMode instead */
  theme: Mode;
  /** @deprecated Use toggleMode instead */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  themeName: 'moonshot',
  toggleMode: () => {},
  setThemeName: () => {},
  theme: 'light',
  toggle: () => {},
});

const MODE_KEY = 'lunar-grid-theme';
const THEME_KEY = 'lunar-grid-theme-name';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<Mode>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(MODE_KEY) as Mode) ?? 'light';
    }
    return 'light';
  });

  const [themeName, setThemeNameState] = useState<ThemeName>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(THEME_KEY) as ThemeName) ?? 'moonshot';
    }
    return 'moonshot';
  });

  useEffect(() => {
    const root = document.documentElement;

    // Mode: light/dark
    if (mode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem(MODE_KEY, mode);

    // Theme name: data-theme attribute
    root.setAttribute('data-theme', themeName);
    localStorage.setItem(THEME_KEY, themeName);
  }, [mode, themeName]);

  const toggleMode = () => setMode((m) => (m === 'dark' ? 'light' : 'dark'));
  const setThemeName = (name: ThemeName) => setThemeNameState(name);

  return (
    <ThemeContext.Provider
      value={{
        mode,
        themeName,
        toggleMode,
        setThemeName,
        // Backwards compat
        theme: mode,
        toggle: toggleMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
